import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import api from '@/service/Api.js'
import {
    chunk,
    difference,
    find,
    findIndex,
    first,
    isEmpty,
    isEqual,
    keyBy,
    last,
    omit,
    pick,
    reverse,
    sortBy,
    startsWith,
    take,
    trim,
    uniq,
} from 'lodash-es'
import {
    calculateAttachmentStyleScores,
    calculateConflictStyleScores,
    calculateLoveRxScores,
    calculateRelAttachmentStyleScores,
    canManageOrgs,
    canManageUsers,
    DistressCategory_Bl,
    getDistressScore,
    getGroupPrivileges,
    getWellBeingScore,
    GlobalUserPrivs,
    memberOfOrgId,
    Privileges,
    ScreenerFrequency,
    WellBeingCategory_Bl,
} from '../../../api/common/sharedUtils.js'
import { getDefaultSubscriptionPlan, SubscriptionPlansByProductId } from '../../../api/common/stripePrices.js'
import { userAttachmentStyleQsById } from '@/store/userAttachmentStyleQs.js'
import { userConflictStyleQsById } from '@/store/userConflictStyleQs.js'
import { userLoveRxQsById } from '@/store/userLoveRxQs.js'
import { relAttachmentStyleQsById } from '@/store/relAttachmentStyleQs.js'
import { relConflictStyleQsById } from '@/store/relConflictStyleQs.js'
import { DateTime } from 'luxon'
import { setNativeAppStatusBarColor, getUserTz } from '@/service/uiUtils.js'
import privateStorage from '@/store/privateStorage.js'
import coachGreetService from '@/service/coachGreetService.js'
import { Capacitor } from '@capacitor/core'
import { InAppReview } from '@capacitor-community/in-app-review'
import pushNotifications from '@/service/pushNotifications.js'
import { Badge } from '@capawesome/capacitor-badge'

const orgCacheLastRefreshedAtKey = 'kn.orgCacheLastRefreshedAt'
const orgCacheTtlMs = 60 * 60 * 1000 // every hour

export const useKinectinStore = defineStore('kinectin', () => {
    const maintenanceMode = ref(false)
    const userIsAuthenticated = ref(false)
    const messagesByThreadId = ref({})
    const messagesFullyLoadedByThreadId = ref({})
    const threads = ref([])
    const threadsById = ref({})
    const threadsByRelationshipId = ref({})
    const relThreadsFullyLoadedByRelId = ref({})
    const openThreadMarkers = ref([])
    const openThreads = ref([])
    const activityOpenThreads = ref([])
    const openThreadsByRelId = ref({})
    const threadsFullyLoaded = ref(false)
    const orgProfilesFullyLoaded = ref(false)
    const selectedThread = ref(null)
    const selectedThreadOrg = ref(null)
    const selectedRelationships = ref([])
    const messages = ref([])
    const userPreferences = ref({})
    const notificationPreferences = ref({})
    const userContacts = ref({})
    const userRelationships = ref([])
    const userRelationshipsById = ref({})

    const ledgerLines = ref([])
    const ledgerLinesFullyLoaded = ref(false)
    const userSummary = ref('')
    const relationshipSummariesById = ref({})
    const userProfile = ref({})
    const revCatInfo = ref({})
    const tokenUsage = ref({})
    const assessmentRetake = ref(false)
    const chatStartPrompt = ref('')
    const drawer = ref(false)
    const orgProfiles = ref([])
    const orgProfilesById = ref({})
    const orgPublicProfilesById = ref({})
    const orgMemberProfilesById = ref({})
    const orgUsersByOrgId = ref({})
    const orgUsersFullyLoadedByOrgId = ref({})
    const orgDocsByOrgId = ref({})
    const orgDocsFullyLoadedByOrgId = ref({})
    const orgNudgesByOrgId = ref({})
    const orgNudgesFullyLoadedByOrgId = ref({})
    const orgNudgeCategoriesByOrgId = ref({})
    const orgNudgeCategoriesFullyLoadedByOrgId = ref({})
    const orgCategoryNudgesByKey = ref({})
    const orgCategoryNudgesFullyLoadedByKey = ref({})
    const orgReportsByOrgId = ref({})
    const orgReportsFullyLoadedByOrgId = ref({})
    const coachGreetMsg = ref(null)
    const recentJournalEntries = ref([])
    const recentJournalEntriesLoaded = ref(false)
    const dailyJournalInsights = ref({})
    const recentBaselineEntries = ref([])
    const recentBaselineEntriesLoaded = ref(false)
    const followedOrgs = ref([])
    const followedOrgsFullyLoaded = ref(false)
    const allUsersList = ref({})
    const allOrgs = ref([])
    const currentActivity = ref(null)
    const orgActivitiesByOrgId = ref({})
    const orgActivitiesFullyLoadedByOrgId = ref({})
    const activitiesData = ref([])
    const orgScreenersByOrgId = ref({})
    const orgScreenersFullyLoadedByOrgId = ref({})
    const orgViewsByOrgId = ref({})
    const orgViewsFullyLoadedByOrgId = ref({})
    const recentScreenerAnswersByKey = ref({})
    const recentScreenerAnswersLoadedByKey = ref({})
    const orgThemeColor = ref(null)

    function clearObject(obj) {
        Object.keys(obj).forEach(key => delete obj[key])
    }

    function getOrgCacheLastRefreshedAt() {
        try {
            const value = localStorage.getItem(orgCacheLastRefreshedAtKey)
            const timestamp = Number(value)
            return Number.isFinite(timestamp) ? timestamp : null
        } catch (err) {
            console.log(orgCacheLastRefreshedAtKey, err)
            return null
        }
    }

    function setOrgCacheLastRefreshedAt(timestamp = Date.now()) {
        try {
            localStorage.setItem(orgCacheLastRefreshedAtKey, String(timestamp))
        } catch (err) {
            console.log(orgCacheLastRefreshedAtKey, err)
        }
    }

    function clearOrgCache() {
        orgProfiles.value = []
        orgProfilesById.value = {}
        orgProfilesFullyLoaded.value = false
        orgPublicProfilesById.value = {}
        orgMemberProfilesById.value = {}
        orgUsersByOrgId.value = {}
        orgUsersFullyLoadedByOrgId.value = {}
        orgDocsByOrgId.value = {}
        orgDocsFullyLoadedByOrgId.value = {}
        orgNudgesByOrgId.value = {}
        orgNudgesFullyLoadedByOrgId.value = {}
        orgNudgeCategoriesByOrgId.value = {}
        orgNudgeCategoriesFullyLoadedByOrgId.value = {}
        orgCategoryNudgesByKey.value = {}
        orgCategoryNudgesFullyLoadedByKey.value = {}
        orgReportsByOrgId.value = {}
        orgReportsFullyLoadedByOrgId.value = {}
        followedOrgs.value = []
        followedOrgsFullyLoaded.value = false
        allOrgs.value = []
        currentActivity.value = null
        orgActivitiesByOrgId.value = {}
        orgActivitiesFullyLoadedByOrgId.value = {}
        activitiesData.value = []
        orgScreenersByOrgId.value = {}
        orgScreenersFullyLoadedByOrgId.value = {}
        orgViewsByOrgId.value = {}
        orgViewsFullyLoadedByOrgId.value = {}
        recentScreenerAnswersByKey.value = {}
        recentScreenerAnswersLoadedByKey.value = {}
        selectedThreadOrg.value = null
        orgThemeColor.value = null

        _orgPublicProfilePromise = {}
        _orgMemberProfilePromise = {}
        activitiesDataPromise = null
        clearObject(_recentScreenerAnswersPromises)
        clearObject(_activitiesById)
        clearObject(_screenersById)
        clearObject(_viewsById)
        clearObject(_userIsMemberOrFollowsOrgId)
    }

    function expireOrgCacheIfNeeded({ now = Date.now() } = {}) {
        const lastRefreshedAt = getOrgCacheLastRefreshedAt()
        if (lastRefreshedAt && now - lastRefreshedAt < orgCacheTtlMs) return false

        clearOrgCache()
        setOrgCacheLastRefreshedAt(now)
        return Boolean(lastRefreshedAt)
    }

    let _recentJournalEntriesPromise = null

    async function loadRecentJournalEntries() {
        if (recentJournalEntriesLoaded.value) return
        if (!_recentJournalEntriesPromise) _recentJournalEntriesPromise = api.getRecentJournalEntries()
        const { response } = await _recentJournalEntriesPromise
        recentJournalEntries.value = response
        _recentJournalEntriesPromise = null
        recentJournalEntriesLoaded.value = true
    }

    async function getTodaysJournalEntry(day) {
        const today = day || DateTime.now().setZone(getUserTz()).toFormat('yyyy-MM-dd')
        const lastEntry = recentJournalEntries.value[0]
        return lastEntry?.day === today ? lastEntry : null
    }

    const _recentScreenerAnswersPromises = {}

    async function loadRecentScreenerAnswers({ orgId, screenerId, noCache } = {}) {
        const key = `${orgId}#${screenerId}`
        if (!noCache && recentScreenerAnswersLoadedByKey.value[key]) return
        if (!_recentScreenerAnswersPromises[key]) {
            _recentScreenerAnswersPromises[key] = api.getRecentScreenerAnswers({ orgId, screenerId })
        }
        const { response } = await _recentScreenerAnswersPromises[key]
        recentScreenerAnswersByKey.value[key] = response
        _recentScreenerAnswersPromises[key] = null
        recentScreenerAnswersLoadedByKey.value[key] = true
    }

    function getRecentScreenerAnswers({ orgId, screenerId }) {
        const key = `${orgId}#${screenerId}`
        return recentScreenerAnswersByKey.value[key] || []
    }

    async function updateJournalEntry({ scores, journalContent, empathyResponse, empathyGeneratedAt }) {
        await loadRecentJournalEntries()
        const day = DateTime.now().setZone(getUserTz()).toFormat('yyyy-MM-dd')
        const wellbeingScore = scores ? getWellBeingScore({ scores }) : null
        const distressScore = scores ? getDistressScore({ scores }) : null
        
        await api.updateJournalEntry({ 
            day, 
            scores, 
            wellbeingScore, 
            distressScore, 
            journalContent,
            empathyResponse,
            empathyGeneratedAt,
        })

        const todayEntry = JSON.parse(JSON.stringify(getTodaysJournalEntry(day))) || { day }
        if (scores) {
            todayEntry.scores = scores
            Object.assign(userProfile.value, { wellbeingScore, distressScore })
        }
        if (journalContent) todayEntry.journalContent = journalContent
        if (empathyResponse) {
            todayEntry.empathyResponse = empathyResponse
            todayEntry.empathyGeneratedAt = empathyGeneratedAt
        }

        recentJournalEntries.value = [todayEntry, ...recentJournalEntries.value.filter(e => e.day !== day)]
    }

    let _recentBaselineEntriesPromise = null

    async function loadRecentBaselineEntries() {
        if (recentBaselineEntriesLoaded.value) return
        if (!_recentBaselineEntriesPromise) _recentBaselineEntriesPromise = api.getRecentBaselineEntries()
        const { response } = await _recentBaselineEntriesPromise
        recentBaselineEntries.value = response
        _recentBaselineEntriesPromise = null
        recentBaselineEntriesLoaded.value = true
    }

    function getThisMonthBaselineEntry() {
        const month = DateTime.now().setZone(getUserTz()).toFormat('yyyy-MM')
        const lastEntry = recentBaselineEntries.value[0]
        return lastEntry?.month === month ? lastEntry : null
    }

    async function updateBaselineEntry({ scores, done }) {
        await loadRecentBaselineEntries()
        const month = DateTime.now().setZone(getUserTz()).toFormat('yyyy-MM')
        const wellbeingScore = getWellBeingScore({ scores, category: WellBeingCategory_Bl })
        const distressScore = getDistressScore({ scores, category: DistressCategory_Bl })
        await api.updateBaselineEntry({ month, scores, wellbeingScore, distressScore, done })

        const existingEntry = recentBaselineEntries.value.find(e => e.month === month)
        recentBaselineEntries.value = [{ ...existingEntry, month, scores }, ...recentBaselineEntries.value.filter(e => e.month !== month)]
        Object.assign(userProfile.value, { wellbeingScore, distressScore })
    }

    async function updateBaselineAnalysis({ month, analysisResult, analysisVersion = '1.0' }) {
        await loadRecentBaselineEntries()
        await api.updateBaselineAnalysis({ month, analysisResult, analysisVersion })

        // Always update the local store with the new analysis
        const analysisGeneratedAt = DateTime.now().toISO()
        const existingEntry = recentBaselineEntries.value.find(e => e.month === month)
        const updatedEntry = existingEntry
            ? { ...existingEntry, analysisResult, analysisGeneratedAt, analysisVersion }
            : { month, analysisResult, analysisGeneratedAt, analysisVersion }

        recentBaselineEntries.value = [updatedEntry, ...recentBaselineEntries.value.filter(e => e.month !== month)]
    }

    async function analyzeDailyJournal({ period, startDate, endDate }) {
        try {
            const analysisKey = `${period}_${startDate}_${endDate}`
            if (dailyJournalInsights.value[analysisKey]) return dailyJournalInsights.value[analysisKey]

            const result = await api.analyzeDailyJournal({ period, startDate, endDate })
            dailyJournalInsights.value[analysisKey] = {
                analysisResult: result.response,
                analysisGeneratedAt: DateTime.now().toISO(),
                period,
                startDate,
                endDate
            }
            return result.response
        } catch (error) {
            console.error('Daily journal analysis failed:', error)
            throw error
        }
    }

    function getDailyJournalInsight({ period, startDate, endDate }) {
        const analysisKey = `${period}_${startDate}_${endDate}`
        return dailyJournalInsights.value[analysisKey] || null
    }

    async function updateScreenerAnswers({ screener, answers, comments }) {
        let timeFragment = null
        const now = DateTime.utc().setZone(getUserTz())
        if (screener.frequency === ScreenerFrequency.Monthly) {
            timeFragment = now.toFormat('yyyy-MM')
        } else if (screener.frequency === ScreenerFrequency.Daily) {
            timeFragment = now.toFormat('yyyy-MM-dd')
        } else {
            throw new Error(`invalid frequency: ${screener.frequency}`)
        }
        const { orgId, screenerId } = screener
        await api.updateScreenerAnswers({ orgId, screenerId, timeFragment, answers, comments })
    }

    let _tokenUsagePromise = null

    async function loadTokenUsage({ noCache } = {}) {
        if (!noCache && !isEmpty(tokenUsage.value)) return
        if (noCache || !_tokenUsagePromise) _tokenUsagePromise = api.getTokenUsage()
        const { response } = await _tokenUsagePromise
        tokenUsage.value = response
        _tokenUsagePromise = null
        tokenUsage.value.noTokensLeft =
            !userHasPrivilege(Privileges.unlimitedTokens) &&
            (tokenUsage.value.monthlyTokens === 0 ||
                (tokenUsage.value.usedTotalTokens >= tokenUsage.value.monthlyTokens))
    }

    let _revCatInfoPromise = null

    async function loadUserRevCatInfo({ noCache } = {}) {
        if (!noCache && !isEmpty(revCatInfo.value)) return
        if (noCache || !_revCatInfoPromise) _revCatInfoPromise = api.getUserRevCatInfo()
        const { response } = await _revCatInfoPromise
        revCatInfo.value = response
        _revCatInfoPromise = null
        const plan = SubscriptionPlansByProductId[revCatInfo.value?.productId] || getDefaultSubscriptionPlan()
        heap.addUserProperties({ subscriptionName: plan.name })
        mixpanel.people.set({ subscriptionName: plan.name })
    }

    let _userProfilePromise = null

    async function loadUserProfile({ noCache } = {}) {
        if (!noCache && !isEmpty(userProfile.value)) return
        if (noCache || !_userProfilePromise) _userProfilePromise = api.getUserProfile()
        const { response } = await _userProfilePromise
        userProfile.value = response
        _userProfilePromise = null
        if (userProfile.value.theme) await privateStorage.setItem('kn.theme', userProfile.value.theme)
        const userTags = {
            birthYear: (userProfile.value.dob || '').split('-')[0],
            gender: userProfile.value.gender || '',
            relationshipStatus: userProfile.value.relationshipStatus,
            ethnicity: userProfile.value.ethnicity,
            orgId: userProfile.value.orgId || '',
        }
        heap.addUserProperties(userTags)
        mixpanel.people.set(userTags)
        await pushNotifications.addTags(pick(userTags, ['orgId', 'gender']))
    }

    async function updateUserProfile({ attrs }) {
        await api.updateUserProfile({ attrs })
        Object.assign(userProfile.value, attrs)
        if (attrs.theme) {
            await privateStorage.setItem('kn.theme', attrs.theme)
            await setNativeAppStatusBarColor()
        }
    }

    function _calcAttachmentStyleResults() {
        const { attachmentStyle } = userProfile.value
        const answeredIds = Object.keys(attachmentStyle || []).filter(e => attachmentStyle[e])
        const results = { answeredIds }
        if (answeredIds.length === Object.keys(userAttachmentStyleQsById).length) {
            const { style, description } = calculateAttachmentStyleScores({ attachmentStyle })
            results.currentResults = { style, description }
        }
        return results
    }

    function _calcConflictStyleResults() {
        const { conflictStyle } = userProfile.value
        const answeredIds = Object.keys(conflictStyle || []).filter(e => conflictStyle[e])
        const results = { answeredIds }
        if (answeredIds.length === Object.keys(userConflictStyleQsById).length) {
            results.currentResults = calculateConflictStyleScores({ conflictStyle })
        }
        return results
    }

    function _calcLoveRxResults() {
        const { loveRx } = userProfile.value
        const answeredIds = Object.keys(loveRx || []).filter(e => loveRx[e])
        const results = { answeredIds }
        if (answeredIds.length === Object.keys(userLoveRxQsById).length) {
            results.currentResults = calculateLoveRxScores({ loveRx })
        }
        return results
    }

    const userAssessmentResults = computed(() => {
        return {
            attachmentStyle: _calcAttachmentStyleResults(),
            conflictStyle: _calcConflictStyleResults(),
            loveRx: _calcLoveRxResults(),
        }
    })

    const relAssessmentResults = computed(() => {
        const results = {}
        for (let rel of userRelationships.value) {
            results[rel.relationshipId] = {
                attachmentStyle: _calcRelAttachmentStyleResults(rel),
                conflictStyle: _calcRelConflictStyleResults(rel),
            }
        }
        return results
    })

    function _calcRelAttachmentStyleResults(rel) {
        const { attachmentStyle } = rel
        const answeredIds = Object.keys(attachmentStyle || []).filter(e => attachmentStyle[e])
        const results = { answeredIds }
        if (answeredIds.length === Object.keys(relAttachmentStyleQsById).length) {
            const { style, description } = calculateRelAttachmentStyleScores({ attachmentStyle })
            results.currentResults = { style, description }
        }
        return results
    }

    function _calcRelConflictStyleResults(rel) {
        const { conflictStyle } = rel
        const answeredIds = Object.keys(conflictStyle || []).filter(e => conflictStyle[e])
        const results = { answeredIds }
        if (answeredIds.length === Object.keys(relConflictStyleQsById).length) {
            results.currentResults = calculateConflictStyleScores({ conflictStyle })
        }
        return results
    }

    let _openThreadMarkersPromise = null

    async function loadOpenThreadMarkers() {
        if (!isEmpty(openThreadMarkers.value)) return
        if (!_openThreadMarkersPromise) _openThreadMarkersPromise = api.getOpenThreadMarkers()
        const { response } = await _openThreadMarkersPromise
        openThreadMarkers.value = response
    }

    watch(openThreadMarkers, async () => {
        if (!Capacitor.isNativePlatform()) return
        const count = (openThreadMarkers.value.length || 0) <= 0 ? 0 : openThreadMarkers.value.length
        await Badge.set({ count })
    })

    const openThreadMarkersByRelId = computed(() => {
        const results = {}
        for (let marker of openThreadMarkers.value) {
            for (let relId of marker.relationshipIds || []) {
                results[relId] = [...(results[relId] || []), marker]
            }
        }
        return results
    })

    async function loadMoreOpenThreads() {
        await loadOpenThreadMarkers()

        const openThreadsById = keyBy(openThreads.value, 'threadId')
        const index = findIndex(openThreadMarkers.value, e => !openThreadsById[e.threadId])
        if (index === -1) return // done loading

        const threadIds = openThreadMarkers.value.slice(index, index + 10).map(e => e.threadId)
        const { response } = await api.getThreads({ threadIds })
        openThreads.value = [...openThreads.value, ...response]
    }

    let activityOpenThreadsPromise = null
    async function loadMoreActivityOpenThreads() {
        if (activityOpenThreadsPromise) return activityOpenThreadsPromise

        activityOpenThreadsPromise = new Promise(async resolve => {
            await Promise.all([loadOpenThreadMarkers(), loadActivitiesData()])

            const actOpenThreadsById = keyBy(activityOpenThreads.value, 'threadId')
            const threadIds = take(
                openThreadMarkers.value
                    .filter(e => e.activityId)
                    .filter(e => !actOpenThreadsById[e.threadId])
                    .map(e => e.threadId),
                10,
            )
            if (isEmpty(threadIds)) return resolve(false)

            const activitiesById = keyBy(activitiesData.value || [], 'activityId')
            const { response } = await api.getThreads({ threadIds })
            activityOpenThreads.value = [
                ...activityOpenThreads.value,
                ...response.map(e => ({ ...e, activity: activitiesById[e.activityId] || {} }))
            ]
            return resolve(true)
        })
        const res = await activityOpenThreadsPromise
        activityOpenThreadsPromise = null
        return res
    }

    async function loadMoreRelationshipOpenThreads({ relationshipId }) {
        await loadOpenThreadMarkers()
        const openThreadsById = keyBy(openThreads.value, 'threadId')

        // get full list of threadIds for relationshipId
        const relThreadIds = []
        for (let e of openThreadMarkers.value) {
            const relIds = e.relationshipIds || []
            if (relIds.includes(relationshipId)) relThreadIds.push(e.threadId)
        }
        // find first index with no thread
        const relThreads = openThreadsByRelId.value[relationshipId] || []
        const relThreadsById = keyBy(relThreads, 'threadId')
        const index = findIndex(relThreadIds, e => !relThreadsById[e])
        if (index === -1) return // done loading

        // get threadIds we need to fetch
        const nextThreadIds = relThreadIds.slice(index, index + 10)
        const missingThreadIds = nextThreadIds.filter(e => !threadsById[e] && !openThreadsById[e])
        let responseThreadsById = {}
        if (!isEmpty(missingThreadIds)) {
            const { response } = await api.getThreads({ threadIds: missingThreadIds })
            responseThreadsById = keyBy(response, 'threadId')
        }

        // patch in the threads
        const nextThreads = []
        for (let e of nextThreadIds) {
            const thread = responseThreadsById[e] || openThreadsById[e] || threadsById[e]
            nextThreads.push(thread)
        }
        openThreadsByRelId.value[relationshipId] = [...relThreads, ...nextThreads]
        openThreadsByRelId.value = JSON.parse(JSON.stringify(openThreadsByRelId.value))
    }

    async function loadUserPreferences() {
        if (!isEmpty(userPreferences.value)) return
        const { response } = await api.getUserPreferences()
        userPreferences.value = omit(response || {}, ['created', 'modified'])
    }

    async function updateUserPreferences({ attrs }) {
        await api.updateUserPreferences({ attrs })
        userPreferences.value = attrs
    }

    async function loadNotificationPreferences() {
        if (!isEmpty(notificationPreferences.value)) return
        const { response } = await api.getNotificationPreferences()
        notificationPreferences.value = omit(response || {}, ['created', 'modified'])
    }

    async function updateNotificationPreferences({ attrs }) {
        await api.updateNotificationPreferences({ attrs })
        notificationPreferences.value = attrs
    }

    async function loadUserContacts() {
        if (!isEmpty(userContacts.value)) return
        const { response } = await api.getUserContacts()
        userContacts.value = response
    }

    async function updateUserContacts({ attrs }) {
        await api.updateUserContacts({ attrs })
        userContacts.value = attrs
    }

    async function createRelationship({ attrs }) {
        const { response } = await api.createRelationship({ attrs })
        userRelationships.value = [...userRelationships.value, response]
        userRelationshipsById.value = keyBy(userRelationships.value, 'relationshipId')
    }

    let _userRelationshipsPromise = null

    async function loadUserRelationships() {
        if (_userRelationshipsPromise) return await _userRelationshipsPromise
        _userRelationshipsPromise = api.getRelationships()
        const { response } = await _userRelationshipsPromise
        userRelationships.value = response
        userRelationshipsById.value = keyBy(userRelationships.value, 'relationshipId')
    }

    async function setSelectedRelationship({ relationshipId }) {
        await loadUserRelationships()
        const rel = userRelationshipsById.value[relationshipId]
        selectedRelationships.value = [rel]
    }

    function clearSelectedRelationships() {
        selectedRelationships.value = []
    }

    function getUserPrivileges() {
        const { privileges, groups, orgId } = userProfile.value
        const privs = [...(privileges || []), ...getGroupPrivileges({ groups })]
        if (orgId) privs.push(`memberOfOrgId:${orgId}`)
        return uniq(privs)
    }

    function userHasPrivilege(privilege) {
        if (GlobalUserPrivs.includes(privilege)) return true
        return getUserPrivileges().includes(privilege)
    }

    function userCanManageUsers() {
        return canManageUsers({ privileges: getUserPrivileges() })
    }

    function userCanManageOrgIds() {
        if (userHasPrivilege(Privileges.manageOrgs)) return true
        return !isEmpty(getUserPrivileges().filter(e => startsWith(e, 'manageOrgId:')))
    }

    function userCanManageOrgs() {
        return canManageOrgs({ privileges: getUserPrivileges() })
    }

    function userIsOrgMember({ orgId }) {
        return memberOfOrgId({ privileges: getUserPrivileges(), orgId })
    }

    async function userFollowsOrg({ orgId }) {
        const { response } = await api.userFollowsOrg({ orgId })
        return response
    }

    async function loadFollowedOrgs() {
        if (followedOrgsFullyLoaded.value) return
        const { response } = await api.getFollowedOrgs()
        followedOrgs.value = response
        followedOrgsFullyLoaded.value = true
    }

    async function getUserOrg({ orgId } = {}) {
        await loadUserProfile()
        orgId = orgId || userProfile.value.orgId
        if (!orgId) return null
        await loadFollowedOrgs()
        return find(followedOrgs.value, e => e.orgId === orgId)
    }

    const currentSubscription = computed(
        () => SubscriptionPlansByProductId[revCatInfo.value?.productId] || getDefaultSubscriptionPlan(),
    )

    const canAddRelationships = computed(() => {
        if (currentSubscription.value && !currentSubscription.value.monthlyTokens) {
            return true
        }
        if (userRelationships.value.length < 5) {
            const hasPriv = userHasPrivilege(Privileges.allowedFiveRels)
            if (hasPriv) return true
        }
        return userRelationships.value.length < (currentSubscription.value?.maxRelationships || 0)
    })

    function clearLedgerLines() {
        ledgerLines.value = []
        ledgerLinesFullyLoaded.value = false
    }

    async function loadMoreLedgerLines() {
        if (ledgerLinesFullyLoaded.value) return

        const lastLedgerLineId = last(ledgerLines.value)?.ledgerLineId
        const { response } = await api.getLedgerLines({ lastLedgerLineId })
        if (isEmpty(response)) {
            ledgerLinesFullyLoaded.value = true
            return
        }
        ledgerLines.value = [...ledgerLines.value, ...response]
        return true
    }

    async function updateRelationship({ relationshipId, attrs }) {
        await api.updateRelationship({ relationshipId, attrs })
        Object.assign(userRelationshipsById.value[relationshipId], attrs)
        userRelationships.value = [...userRelationships.value]
    }

    async function loadUserSummary() {
        if (!isEmpty(userSummary.value)) return
        const { response } = await api.getUserSummary()
        userSummary.value = trim(response)
    }

    async function updateUserSummary({ summary, fromMessages }) {
        const { response } = await api.updateUserSummary({ summary, fromMessages })
        userSummary.value = fromMessages ? response : summary
    }

    async function loadRelationshipSummary({ relationshipId }) {
        if (!isEmpty(relationshipSummariesById.value[relationshipId])) return
        const { response } = await api.getRelationshipSummary({ relationshipId })
        relationshipSummariesById.value[relationshipId] = trim(response)
    }

    async function updateRelationshipSummary({ relationshipId, summary }) {
        await api.updateRelationshipSummary({ relationshipId, summary })
        relationshipSummariesById.value[relationshipId] = summary
    }

    async function loadMoreThreads() {
        if (threadsFullyLoaded.value) return

        const lastThreadId = last(threads.value)?.threadId
        const { response } = await api.getThreads({ lastThreadId })
        if (isEmpty(response)) {
            threadsFullyLoaded.value = true
            return
        }
        threads.value = [...threads.value, ...response]
        threadsById.value = keyBy(threads.value, 'threadId')
        return true
    }

    async function loadMoreOrgProfiles() {
        if (orgProfilesFullyLoaded.value) return

        const lastOrgId = last(orgProfiles.value)?.orgId
        const { response } = await api.getOrgProfiles({ lastOrgId })
        if (isEmpty(response)) {
            orgProfilesFullyLoaded.value = true
            return
        }
        orgProfiles.value = [...orgProfiles.value, ...response]
        orgProfilesById.value = keyBy(orgProfiles.value, 'orgId')
        return true
    }

    async function loadMoreOrgUsers({ orgId }) {
        if (orgUsersFullyLoadedByOrgId.value[orgId]) return

        const lastEmail = last(orgUsersByOrgId.value[orgId])?.email
        const { response } = await api.getOrgUsers({ orgId, lastEmail })
        if (isEmpty(response)) {
            orgUsersFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgUsersByOrgId.value[orgId] = [...(orgUsersByOrgId.value[orgId] || []), ...response]
        return true
    }

    async function loadAllOrgUsers({ orgId }) {
        if (orgUsersFullyLoadedByOrgId.value[orgId]) return

        const { response } = await api.getAllOrgUsers({ orgId })
        orgUsersFullyLoadedByOrgId.value[orgId] = true
        orgUsersByOrgId.value[orgId] = [...(orgUsersByOrgId.value[orgId] || []), ...response]
    }

    async function loadMoreOrgDocs({ orgId }) {
        if (orgDocsFullyLoadedByOrgId.value[orgId]) return

        const lastDocId = last(orgDocsByOrgId.value[orgId])?.docId
        const { response } = await api.getOrgDocs({ orgId, lastDocId })
        if (isEmpty(response)) {
            orgDocsFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgDocsByOrgId.value[orgId] = [...(orgDocsByOrgId.value[orgId] || []), ...response]
        return true
    }

    async function createOrgDocs({ orgId, docInfoList }) {
        const { response } = await api.createOrgDocs({ orgId, docInfoList })
        orgDocsByOrgId.value[orgId] = [...response, ...(orgDocsByOrgId.value[orgId] || [])]
        return response
    }

    async function deleteOrgDoc({ orgId, docId }) {
        await api.deleteOrgDoc({ orgId, docId })
        orgDocsByOrgId.value[orgId] = orgDocsByOrgId.value[orgId].filter(e => e.docId !== docId)
    }

    async function createOrgReport({ orgId, reportType }) {
        const { response } = await api.createOrgReport({ orgId, reportType })
        orgReportsByOrgId.value[orgId] = [response, ...(orgReportsByOrgId.value[orgId] || [])]
        return response
    }

    async function resetOrgReports({ orgId }) {
        orgReportsByOrgId.value[orgId] = []
        orgReportsFullyLoadedByOrgId.value[orgId] = false
    }

    async function loadMoreOrgReports({ orgId }) {
        if (orgReportsFullyLoadedByOrgId.value[orgId]) return

        const lastReportId = last(orgReportsByOrgId.value[orgId])?.reportId
        const { response } = await api.getOrgReports({ orgId, lastReportId })
        if (isEmpty(response)) {
            orgReportsFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgReportsByOrgId.value[orgId] = [...(orgReportsByOrgId.value[orgId] || []), ...response]
        return true
    }

    let activitiesDataPromise = null
    async function loadActivitiesData() {
        if (activitiesDataPromise) return activitiesDataPromise

        activitiesDataPromise = new Promise(async resolve => {
            await loadFollowedOrgs()

            async function fetchActivities(url) {
                try {
                    const response = await fetch(url)
                    if (!response.ok) return []
                    return await response.json()
                } catch (error) {
                    console.error('Error fetching version data:', error)
                    return []
                }
            }

            const activitiesArray = []
            for (let batch of chunk(followedOrgs.value || [], 10)) {
                const promises = batch.map(e => api.getOrgActivitiesDownloadUrl({ orgId: e.orgId }))
                const responses = await Promise.all(promises)
                const urls = responses.map(e => e.response)
                const arrays = await Promise.all(urls.map(url => fetchActivities(url)))
                activitiesArray.push(...arrays)
            }
            for (let e of activitiesArray) activitiesData.value.push(...e)
            resolve()
        })
        return activitiesDataPromise
    }

    async function resolveOrgUsers({ orgId, usersRef }) {
        const userIds = usersRef.value.filter(e => !e.firstName).map(e => e.userId)
        if (isEmpty(userIds)) return
        const { response } = await api.resolveOrgUsers({ orgId, userIds })
        const userDataById = keyBy(response, 'userId')
        usersRef.value.map(e => Object.assign(e, userDataById[e.userId]))
    }

    async function getOrgProfile({ orgId }) {
        const org = orgProfilesById.value[orgId]
        if (org) return org
        const { response } = await api.getOrgProfile({ orgId })
        orgProfilesById.value[response.orgId] = response
        return response
    }

    let _orgPublicProfilePromise = {}

    async function getOrgPublicProfile({ orgId }) {
        const org = orgPublicProfilesById.value[orgId]
        if (org) return org
        if (!_orgPublicProfilePromise[orgId]) _orgPublicProfilePromise[orgId] = api.getOrgPublicProfile({ orgId })
        const { response } = await _orgPublicProfilePromise[orgId]
        orgPublicProfilesById.value[response.orgId] = response
        return response
    }

    let _orgMemberProfilePromise = {}

    async function getOrgMemberProfile({ orgId }) {
        const org = orgMemberProfilesById.value[orgId]
        if (org) return org
        if (!_orgMemberProfilePromise[orgId]) _orgMemberProfilePromise[orgId] = api.getOrgMemberProfile({ orgId })
        const { response } = await _orgMemberProfilePromise[orgId]
        orgMemberProfilesById.value[response.orgId] = response
        return response
    }

    async function createOrgProfile({ attrs }) {
        const { response } = await api.createOrgProfile({ attrs })
        orgProfilesById.value[response.orgId] = response
        orgProfiles.value = [response, ...orgProfiles.value]
        allOrgs.value = sortBy(
            [...allOrgs.value, pick(response, 'orgId', 'name', 'profileImageKey')],
            'name',
        )
        return response
    }

    async function updateOrgProfile({ orgId, attrs }) {
        await api.updateOrgProfile({ orgId, attrs })
        const orgProfile = orgProfilesById.value[orgId] || await getOrgProfile({ orgId })
        Object.assign(orgProfile, attrs)
        orgProfilesById.value[orgId] = { ...orgProfile }
        orgProfiles.value = [...orgProfiles.value]
        const followedOrg = find(followedOrgs.value, e => e.orgId === orgId)
        if (followedOrg) Object.assign(followedOrg, attrs)
        const orgMemberProfile = orgMemberProfilesById.value[orgId]
        if (orgMemberProfile) Object.assign(orgMemberProfile, attrs)
        return orgProfile
    }

    async function deleteOrgUser({ orgId, email }) {
        await api.deleteOrgUser({ orgId, email })
        orgUsersByOrgId.value[orgId] = (orgUsersByOrgId.value[orgId] || []).filter(e => e.email !== email)
    }

    async function createOrgNudge({ orgId, attrs }) {
        const { response } = await api.createOrgNudge({ orgId, attrs })
        orgNudgesByOrgId.value[orgId] = [...(orgNudgesByOrgId.value[orgId] || []), response]
        return response
    }

    async function updateOrgNudge({ orgId, nudgeId, attrs }) {
        await api.updateOrgNudge({ orgId, nudgeId, attrs })
        const nudge = orgNudgesByOrgId.value[orgId].find(e => e.nudgeId === nudgeId)
        Object.assign(nudge, attrs)
    }

    async function createOrgNudgeCategory({ orgId, attrs }) {
        const { response } = await api.createOrgNudgeCategory({ orgId, attrs })
        orgNudgeCategoriesByOrgId.value[orgId] = [...(orgNudgeCategoriesByOrgId.value[orgId] || []), response]
        return response
    }

    async function updateOrgNudgeCategory({ orgId, categoryId, attrs }) {
        await api.updateOrgNudgeCategory({ orgId, categoryId, attrs })
        const category = orgNudgeCategoriesByOrgId.value[orgId].find(e => e.categoryId === categoryId)
        Object.assign(category, attrs)
    }

    async function loadMoreOrgNudgeCategories({ orgId }) {
        const canUseNudge = await userCanUseNudge({ orgId })
        if (!canUseNudge) {
            orgNudgeCategoriesByOrgId.value[orgId] = []
            orgNudgeCategoriesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        if (orgNudgeCategoriesFullyLoadedByOrgId.value[orgId]) return

        const lastCategoryId = last(orgNudgeCategoriesByOrgId.value[orgId])?.categoryId
        const { response } = await api.getOrgNudgeCategories({ orgId, lastCategoryId })
        if (isEmpty(response)) {
            orgNudgeCategoriesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgNudgeCategoriesByOrgId.value[orgId] = [...(orgNudgeCategoriesByOrgId.value[orgId] || []), ...response]
        return true
    }

    function orgCategoryNudgeKey({ orgId, categoryId }) {
        return [orgId, categoryId].join('#')
    }

    async function loadMoreOrgCategoryNudges({ orgId, categoryId }) {
        const key = orgCategoryNudgeKey({ orgId, categoryId })
        const canUseNudge = await userCanUseNudge({ orgId })
        if (!canUseNudge) {
            orgCategoryNudgesByKey.value[key] = []
            orgCategoryNudgesFullyLoadedByKey.value[key] = true
            return
        }
        if (orgCategoryNudgesFullyLoadedByKey.value[key]) return

        const lastNudgeId = last(orgCategoryNudgesByKey.value[key])?.nudgeId
        const { response } = await api.getOrgCategoryNudges({ orgId, categoryId, lastNudgeId })
        if (isEmpty(response)) {
            orgCategoryNudgesFullyLoadedByKey.value[key] = true
            return
        }
        orgCategoryNudgesByKey.value[key] = [...(orgCategoryNudgesByKey.value[key] || []), ...response]
        return true
    }

    async function userCanUseNudge({ orgId }) {
        return userIsMemberOrFollows({ orgId })
    }

    async function userCanUseActivity({ orgId }) {
        return userIsMemberOrFollows({ orgId })
    }

    function setCurrentActivity({ activity }) {
        currentActivity.value = activity
    }

    const _activitiesById = {}

    async function getOrgActivity({ orgId, activityId }) {
        const activity = _activitiesById[activityId]
        if (activity) return activity
        const { response } = await api.getOrgActivity({ orgId, activityId })
        _activitiesById[activityId] = response
        return response
    }

    async function loadAndSetCurrentActivity({ orgId, activityId }) {
        if (!activityId) return setCurrentActivity({ activity: null })

        const activity = await getOrgActivity({ orgId, activityId })
        setCurrentActivity({ activity })
    }

    async function createOrgActivity({ orgId, attrs }) {
        const { response } = await api.createOrgActivity({ orgId, attrs })
        orgActivitiesByOrgId.value[orgId] = [...(orgActivitiesByOrgId.value[orgId] || []), response]
        return response
    }

    async function updateOrgActivity({ orgId, activityId, attrs }) {
        await api.updateOrgActivity({ orgId, activityId, attrs })
        const activity = orgActivitiesByOrgId.value[orgId].find(e => e.activityId === activityId)
        Object.assign(activity, attrs)
    }

    async function loadMoreOrgActivities({ orgId }) {
        const canUseActivity = await userCanUseActivity({ orgId })
        if (!canUseActivity) {
            orgActivitiesByOrgId.value[orgId] = []
            orgActivitiesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        if (orgActivitiesFullyLoadedByOrgId.value[orgId]) return

        const lastActivityId = last(orgActivitiesByOrgId.value[orgId])?.activityId
        const { response } = await api.getOrgActivities({ orgId, lastActivityId })
        if (isEmpty(response)) {
            orgActivitiesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgActivitiesByOrgId.value[orgId] = [...(orgActivitiesByOrgId.value[orgId] || []), ...response]
        return true
    }

    const _screenersById = {}

    async function getOrgScreener({ orgId, screenerId }) {
        const screener = _screenersById[screenerId]
        if (screener) return screener
        const { response } = await api.getOrgScreener({ orgId, screenerId })
        _screenersById[screenerId] = response
        return response
    }

    async function createOrgScreener({ orgId, attrs }) {
        const { response } = await api.createOrgScreener({ orgId, attrs })
        orgScreenersByOrgId.value[orgId] = [...(orgScreenersByOrgId.value[orgId] || []), response]
        return response
    }

    async function updateOrgScreener({ orgId, screenerId, attrs }) {
        await api.updateOrgScreener({ orgId, screenerId, attrs })
        const screener = orgScreenersByOrgId.value[orgId].find(e => e.screenerId === screenerId)
        Object.assign(screener, attrs)
    }

    async function loadMoreOrgScreeners({ orgId }) {
        const canUseScreener = userIsOrgMember({ orgId })
        if (!canUseScreener) {
            orgScreenersByOrgId.value[orgId] = []
            orgScreenersFullyLoadedByOrgId.value[orgId] = true
            return
        }
        if (orgScreenersFullyLoadedByOrgId.value[orgId]) return

        const lastScreenerId = last(orgScreenersByOrgId.value[orgId])?.screenerId
        const { response } = await api.getOrgScreeners({ orgId, lastScreenerId })
        if (isEmpty(response)) {
            orgScreenersFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgScreenersByOrgId.value[orgId] = [...(orgScreenersByOrgId.value[orgId] || []), ...response]
        response.map(e => _screenersById[e.screenerId] = e)
        return true
    }

    async function loadAllOrgScreeners({ orgId }) {
        while (!orgScreenersFullyLoadedByOrgId.value[orgId]) {
            await loadMoreOrgScreeners({ orgId })
        }
    }

    const _viewsById = {}

    async function getOrgView({ orgId, viewId }) {
        const view = _viewsById[viewId]
        if (view) return view
        const { response } = await api.getOrgView({ orgId, viewId })
        _viewsById[viewId] = response
        return response
    }

    async function createOrgView({ orgId, attrs }) {
        const { response } = await api.createOrgView({ orgId, attrs })
        orgViewsByOrgId.value[orgId] = [...(orgViewsByOrgId.value[orgId] || []), response]
        return response
    }

    async function updateOrgView({ orgId, viewId, attrs }) {
        await api.updateOrgView({ orgId, viewId, attrs })
        const view = orgViewsByOrgId.value[orgId].find(e => e.viewId === viewId)
        Object.assign(view, attrs)
    }

    async function loadMoreOrgViews({ orgId }) {
        const canUseView = userIsOrgMember({ orgId })
        if (!canUseView) {
            orgViewsByOrgId.value[orgId] = []
            orgViewsFullyLoadedByOrgId.value[orgId] = true
            return
        }
        if (orgViewsFullyLoadedByOrgId.value[orgId]) return

        const lastViewId = last(orgViewsByOrgId.value[orgId])?.viewId
        const { response } = await api.getOrgViews({ orgId, lastViewId })
        if (isEmpty(response)) {
            orgViewsFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgViewsByOrgId.value[orgId] = [...(orgViewsByOrgId.value[orgId] || []), ...response]
        response.map(e => _viewsById[e.viewId] = e)
        return true
    }

    async function loadAllOrgViews({ orgId }) {
        while (!orgViewsFullyLoadedByOrgId.value[orgId]) {
            await loadMoreOrgViews({ orgId })
        }
    }

    const _userIsMemberOrFollowsOrgId = {}

    async function userIsMemberOrFollows({ orgId }) {
        if (orgId in _userIsMemberOrFollowsOrgId) return _userIsMemberOrFollowsOrgId[orgId]
        const [isOrgMember, followsOrg] = await Promise.all([
            userIsOrgMember({ orgId }),
            userFollowsOrg({ orgId }),
        ])
        _userIsMemberOrFollowsOrgId[orgId] = isOrgMember || followsOrg
        return _userIsMemberOrFollowsOrgId[orgId]
    }

    async function loadMoreOrgNudges({ orgId }) {
        const canUseNudge = await userCanUseNudge({ orgId })
        if (!canUseNudge) {
            orgNudgesByOrgId.value[orgId] = []
            orgNudgesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        if (orgNudgesFullyLoadedByOrgId.value[orgId]) return

        const lastNudgeId = last(orgNudgesByOrgId.value[orgId])?.nudgeId
        const { response } = await api.getOrgNudges({ orgId, lastNudgeId })
        if (isEmpty(response)) {
            orgNudgesFullyLoadedByOrgId.value[orgId] = true
            return
        }
        orgNudgesByOrgId.value[orgId] = [...(orgNudgesByOrgId.value[orgId] || []), ...response]
        return true
    }

    async function loadMoreRelationshipThreads({ relationshipId }) {
        if (relThreadsFullyLoadedByRelId.value[relationshipId]) return

        const threads = threadsByRelationshipId.value[relationshipId] || []
        const lastThreadId = last(threads)?.threadId
        const { response } = await api.getRelationshipThreads({ relationshipId, lastThreadId })
        if (isEmpty(response)) {
            relThreadsFullyLoadedByRelId.value[relationshipId] = true
            return
        }
        threads.push(...response)
        threadsByRelationshipId.value[relationshipId] = [...threads]
        return true
    }

    async function selectThread({ threadId }) {
        if (!threadId) {
            selectedThread.value = null
            currentActivity.value = null
            messages.value = []
            return
        }
        await loadUserRelationships()
        const thread = threadsById.value[threadId]
        if (thread) {
            selectedThread.value = thread
            const relIds = thread.relationshipIds || []
            selectedRelationships.value = relIds.map(e => userRelationshipsById.value[e])
            messages.value = [...(messagesByThreadId.value[threadId] || [])]
            await loadAndSetCurrentActivity(selectedThread.value)
            return
        }
        const { response } = await api.getThread({ threadId })
        selectedThread.value = response
        threadsById.value[threadId] = response
        const relIds = response.relationshipIds || []
        selectedRelationships.value = relIds.map(e => userRelationshipsById.value[e])
        messages.value = [...(messagesByThreadId.value[threadId] || [])]
        await loadAndSetCurrentActivity(selectedThread.value)
    }

    async function createThread({ prompt, orgId, nudgeId, activityId, agentMode, withMessages }) {
        messages.value = [...(withMessages || [])]
        const relationshipIds = selectedRelationships.value.map(e => e.relationshipId)
        const attrs = { prompt, relationshipIds, orgId, nudgeId, activityId, agentMode }
        const { response: thread } = await api.createThread({ attrs })
        const threadId = thread.threadId
        messagesByThreadId.value[threadId] = [...messages.value]
        messagesFullyLoadedByThreadId[threadId] = true
        threads.value = [thread, ...threads.value]
        threadsById.value[threadId] = thread
        openThreadMarkers.value = [{ threadId, relationshipIds }, ...openThreadMarkers.value]
        openThreads.value = [thread, ...openThreads.value]
        for (let relId of thread.relationshipIds || []) {
            openThreadsByRelId.value[relId] = [thread, ...(openThreadsByRelId.value[relId] || [])]
        }
        if (activityId) {
            const activity = await getOrgActivity({ orgId, activityId })
            activityOpenThreads.value = [{...thread, activity}, ...activityOpenThreads.value]
        }
        return thread
    }

    async function updateThread({ threadId, attrs = {} }) {
        await api.updateThread({ threadId, attrs })
        let thread = threadsById.value[threadId]
        if (!thread) {
            const { response } = await api.getThreads({ threadIds: [threadId] })
            thread = response[0]
            threadsById.value[threadId] = thread
        }
        const newRelIds = difference(attrs.relationshipIds, thread.relationshipIds)

        Object.assign(thread, attrs)
        threads.value = [...threads.value]
        if (selectedThread.value?.threadId === threadId) selectedThread.value = thread

        if (!isEmpty(newRelIds)) {
            const marker = openThreadMarkers.value.find(e => e.threadId === threadId)
            marker.relationshipIds = thread.relationshipIds
            openThreadMarkers.value = [...openThreadMarkers.value]
            for (let relId of newRelIds) {
                openThreadsByRelId.value[relId] = null
                threadsByRelationshipId.value[relId] = null
                relThreadsFullyLoadedByRelId.value[relId] = false
                await loadMoreRelationshipThreads({ relationshipId: relId })
            }
        }
    }

    async function closeThread({ threadId }) {
        const { response } = await api.closeThread({ threadId })
        const thread = threadsById.value[threadId]
        if (threadId === selectedThread.value?.threadId) {
            Object.assign(selectedThread.value, response)
        }
        if (thread) Object.assign(thread, response)
        threadsById.value[threadId] = response
        threads.value = [...threads.value]
        _removeFromOpenThreadsAndMarkers(threadId)
        await loadTokenUsage({ noCache: true })
    }

    async function deleteThread({ threadId, llmThreadId }) {
        await api.deleteThread({ threadId, llmThreadId })
        threads.value = threads.value.filter(e => e.threadId !== threadId)
        const thread = threadsById.value[threadId]
        for (let relId of thread?.relationshipIds || []) {
            if (isEmpty(threadsByRelationshipId.value[relId])) continue
            threadsByRelationshipId.value[relId] =
                threadsByRelationshipId.value[relId].filter(e => e.threadId !== threadId)
        }
        _removeFromOpenThreadsAndMarkers(threadId)
    }

    function _removeFromOpenThreadsAndMarkers(threadId) {
        const thread = threadsById.value[threadId]
        openThreadMarkers.value = openThreadMarkers.value.filter(e => e.threadId !== threadId)
        openThreads.value = openThreads.value.filter(e => e.threadId !== threadId)
        activityOpenThreads.value = activityOpenThreads.value.filter(e => e.threadId !== threadId)
        for (let relId of thread?.relationshipIds || []) {
            if (isEmpty(openThreadsByRelId.value[relId])) continue
            openThreadsByRelId.value[relId] =
                openThreadsByRelId.value[relId].filter(e => e.threadId !== threadId)
        }
    }

    async function loadMoreMessages() {
        const threadId = selectedThread.value?.threadId
        if (!threadId) {
            messages.value = []
            return
        }
        if (messagesFullyLoadedByThreadId[threadId]) {
            messages.value = [...(messagesByThreadId.value[threadId] || [])]
            return
        }

        const lastMessageId = first(messages.value)?.messageId
        const { response } = await api.getMessages({ threadId, lastMessageId })
        if (isEmpty(response)) {
            messagesFullyLoadedByThreadId[threadId] = true
            return
        }

        messages.value.unshift(...reverse(response))
        messages.value = [...messages.value]
        messagesByThreadId.value[threadId] = messages.value
        return true
    }

    async function streamCoachResponse({ prompt, userMessage, assistantMessage, _coachGreetMsg, responseCallback }) {
        try {
            const {
                response: {
                    coachGreetMessage: cgMsg,
                    userMessage: uMsg,
                    assistantMessage: aMsg,
                },
            } = await api.streamCoachResponse({
                threadId: selectedThread.value.threadId,
                llmThreadId: selectedThread.value.llmThreadId,
                coachGreetMsg: _coachGreetMsg,
                prompt,
                callbackFn: ({ delta, status, toolOutput }) => {
                    assistantMessage.content += delta
                    assistantMessage.status = status
                    messages.value = [...messages.value] // forces reactivity
                },
            })
            if (cgMsg) Object.assign(_coachGreetMsg, cgMsg)
            Object.assign(userMessage, uMsg)
            Object.assign(assistantMessage, aMsg)
            if (responseCallback) responseCallback({ assistantMsg: '' + assistantMessage.content })
        } catch (err) {
            console.error(err)
            assistantMessage.error = err
            throw err
        } finally {
            delete assistantMessage.processing
            delete assistantMessage.status
            messages.value = [...messages.value]
        }
        await loadTokenUsage({ noCache: true })
    }

    async function deleteMessages({ threadId, llmThreadId, fromMessageId, fromLlmMessageId }) {
        const { response } = await api.deleteMessages({ threadId, llmThreadId, fromMessageId, fromLlmMessageId })
        const thread = find(threads.value, { threadId })
        if (thread) {
            Object.assign(thread, response)
            threads.value = [...threads.value]
        }
        if (threadId === selectedThread.value?.threadId) {
            Object.assign(selectedThread.value, response)
        }
        threadsById.value[threadId] = response
        const relIds = response.relationshipIds || []
        selectedRelationships.value = relIds.map(e => userRelationshipsById.value[e])
    }

    async function clearCoachGreet() {
        await coachGreetService.clear()
    }

    watch(selectedThread, async () => {
        const orgId = selectedThread.value?.orgId
        if (!orgId) return selectedThreadOrg.value = null
        selectedThreadOrg.value = await getOrgPublicProfile({ orgId })
    })

    async function inAppReviewPromptable() {
        if (!Capacitor.isNativePlatform()) return false
        if (!Capacitor.isPluginAvailable('InAppReview')) return false
        await loadUserProfile()
        if (!userProfile.value.lastReviewRequest) return true

        // true if the last review was more than 4 months ago
        return DateTime.fromISO(userProfile.value.lastReviewRequest) < DateTime.now().minus({ months: 4 })
    }

    async function promptForInAppReview() {
        if (!(await inAppReviewPromptable())) return
        try {
            await InAppReview.requestReview()
        } catch (e) {
            // ignore... nothing we can do
        } finally {
            await updateUserProfile({ attrs: { lastReviewRequest: DateTime.utc().toString() } })
        }
    }

    async function adminLoadAllOrgs() {
        if (!isEmpty(allOrgs.value)) return
        const { response } = await api.adminGetAllOrgs()
        allOrgs.value = response
    }

    async function adminAllUsersListLoadMore({ emailPrefix }) {
        if (!emailPrefix) emailPrefix = undefined
        if (!isEqual(allUsersList.value.emailPrefix, emailPrefix)) {
            allUsersList.value = {}
            allUsersList.value.emailPrefix = emailPrefix
        }
        if (allUsersList.value.fullyLoaded) return

        const list = allUsersList.value.list || []
        const lastEmail = last(list)?.email
        const { response } = await api.adminGetAllUsers({ lastEmail, emailPrefix })
        if (isEmpty(response)) {
            allUsersList.value.fullyLoaded = true
            return
        }
        allUsersList.value.list = [...list, ...response]
        return true
    }

    async function adminUpdateUsers({ users }) {
        const { response: updatedUsers } = await api.adminUpdateUsers({ users })
        const usersByEmail = keyBy(allUsersList.value.list, 'email')
        for (const user of updatedUsers) Object.assign(usersByEmail[user.email], user)
        allUsersList.value = JSON.parse(JSON.stringify(allUsersList.value))
    }

    async function adminDeleteUser({ email }) {
        const { response: updatedUser } = await api.adminDeleteUser({ email })
        const usersByEmail = keyBy(allUsersList.value.list, 'email')
        Object.assign(usersByEmail[email], updatedUser)
        allUsersList.value = JSON.parse(JSON.stringify(allUsersList.value))
    }

    return {
        maintenanceMode,
        userIsAuthenticated,
        userProfile,
        userPreferences,
        notificationPreferences,
        userContacts,
        userRelationships,
        userRelationshipsById,
        userSummary,
        relationshipSummariesById,
        threads,
        openThreadMarkers,
        openThreads,
        openThreadsByRelId,
        selectedThread,
        selectedThreadOrg,
        selectedRelationships,
        messages,
        messagesByThreadId,
        ledgerLines,
        ledgerLinesFullyLoaded,
        revCatInfo,
        tokenUsage,
        canAddRelationships,
        currentSubscription,
        userAssessmentResults,
        relAssessmentResults,
        assessmentRetake,
        threadsByRelationshipId,
        openThreadMarkersByRelId,
        chatStartPrompt,
        drawer,
        orgProfiles,
        orgProfilesById,
        orgProfilesFullyLoaded,
        orgMemberProfilesById,
        orgUsersByOrgId,
        orgUsersFullyLoadedByOrgId,
        orgDocsByOrgId,
        orgDocsFullyLoadedByOrgId,
        orgReportsByOrgId,
        orgReportsFullyLoadedByOrgId,
        coachGreetMsg,
        recentJournalEntries,
        recentJournalEntriesLoaded,
        dailyJournalInsights,
        recentBaselineEntries,
        recentBaselineEntriesLoaded,
        orgNudgesByOrgId,
        orgNudgesFullyLoadedByOrgId,
        orgNudgeCategoriesByOrgId,
        orgNudgeCategoriesFullyLoadedByOrgId,
        orgCategoryNudgesByKey,
        orgCategoryNudgesFullyLoadedByKey,
        followedOrgs,
        allUsersList,
        allOrgs,
        currentActivity,
        orgActivitiesByOrgId,
        orgActivitiesFullyLoadedByOrgId,
        activitiesData,
        activityOpenThreads,
        orgScreenersByOrgId,
        orgScreenersFullyLoadedByOrgId,
        orgViewsByOrgId,
        orgViewsFullyLoadedByOrgId,
        loadActivitiesData,
        adminLoadAllOrgs,
        adminAllUsersListLoadMore,
        adminUpdateUsers,
        adminDeleteUser,
        loadMoreOrgProfiles,
        loadMoreOrgUsers,
        loadAllOrgUsers,
        loadMoreOrgDocs,
        createOrgDocs,
        deleteOrgDoc,
        getOrgProfile,
        getOrgPublicProfile,
        getOrgMemberProfile,
        createOrgProfile,
        updateOrgProfile,
        deleteOrgUser,
        loadUserProfile,
        updateUserProfile,
        loadMoreOpenThreads,
        loadMoreActivityOpenThreads,
        loadMoreRelationshipOpenThreads,
        loadUserPreferences,
        updateUserPreferences,
        loadNotificationPreferences,
        updateNotificationPreferences,
        loadUserContacts,
        updateUserContacts,
        loadUserRelationships,
        createRelationship,
        updateRelationship,
        loadUserSummary,
        updateUserSummary,
        loadRelationshipSummary,
        updateRelationshipSummary,
        selectThread,
        loadMoreThreads,
        createThread,
        updateThread,
        closeThread,
        deleteThread,
        loadMoreMessages,
        streamCoachResponse,
        deleteMessages,
        clearLedgerLines,
        loadMoreLedgerLines,
        loadUserRevCatInfo,
        loadTokenUsage,
        userHasPrivilege,
        userCanManageUsers,
        userCanManageOrgIds,
        loadMoreRelationshipThreads,
        loadOpenThreadMarkers,
        getUserPrivileges,
        userCanManageOrgs,
        createOrgReport,
        loadMoreOrgReports,
        resetOrgReports,
        setSelectedRelationship,
        clearSelectedRelationships,
        loadRecentJournalEntries,
        updateJournalEntry,
        getTodaysJournalEntry,
        analyzeDailyJournal,
        getDailyJournalInsight,
        loadRecentBaselineEntries,
        updateBaselineEntry,
        updateBaselineAnalysis,
        getThisMonthBaselineEntry,
        clearCoachGreet,
        resolveOrgUsers,
        createOrgNudge,
        updateOrgNudge,
        loadMoreOrgNudges,
        createOrgNudgeCategory,
        updateOrgNudgeCategory,
        loadMoreOrgNudgeCategories,
        orgCategoryNudgeKey,
        loadMoreOrgCategoryNudges,
        userCanUseNudge,
        userCanUseActivity,
        getOrgActivity,
        loadAndSetCurrentActivity,
        userFollowsOrg,
        loadFollowedOrgs,
        getUserOrg,
        inAppReviewPromptable,
        promptForInAppReview,
        createOrgActivity,
        updateOrgActivity,
        loadMoreOrgActivities,
        createOrgScreener,
        updateOrgScreener,
        loadMoreOrgScreeners,
        loadAllOrgScreeners,
        getOrgScreener,
        updateScreenerAnswers,
        loadRecentScreenerAnswers,
        getRecentScreenerAnswers,
        recentScreenerAnswersByKey,
        orgThemeColor,
        createOrgView,
        updateOrgView,
        loadMoreOrgViews,
        loadAllOrgViews,
        getOrgView,
        expireOrgCacheIfNeeded,
    }
})
